import { useState, useEffect, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  //ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Box,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  //ChevronRight as ChevronRightIcon,
  // Notifications as NotificationsIcon,
  Search as SearchIcon,
  // Person as PersonIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import {
  selectUserData,
  selectUserMenuFiltered,
  setDisplayErrorSnackBar,
} from "../../features/userSlice";
import {
  clearSelectedWorkspace,
  resetWorkspaceState,
} from "../../features/workspaceSlice";
import { loginService } from "../../services/login.service";
import WorkspaceDropdown from "../../core/layouts/shared/WorkspaceDropdown";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

import {
  colors,
  spacing,
  typography,
} from "../../assets/styles/tokens";

const ALLOWED_MENU_ITEMS = ["Biz Leader", "biz_development"];

// Recursively check whether the given pathname matches an item or any of its descendants.
const pathMatchesItem = (pathname: string, item: any): boolean => {
  if (item.path && item.path !== "#" && pathname.includes(item.path))
    return true;
  if (item.children?.length) {
    return item.children.some((child: any) => pathMatchesItem(pathname, child));
  }
  return false;
};

// Return the top-level menu label whose subtree contains the current pathname.
const getActiveSectionLabel = (
  pathname: string,
  menuItems: any[],
): string | null => {
  const item = menuItems?.find((m: any) => pathMatchesItem(pathname, m));
  return item?.label ?? null;
};
const SKIP_LABELS = ["website_projection", "forecaster", "Incentivised_traffic"];

const filterMenuRecursively = (
  menuItems: any[],
  isChildOfAllowed = false,
): any[] => {
  if (!menuItems) return [];
  return menuItems
    .map((item: any) => {
      if (SKIP_LABELS.includes(item.label)) return null;
      if (isChildOfAllowed) {
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: filterMenuRecursively(item.children, true),
          };
        }
        return item;
      }
      if (ALLOWED_MENU_ITEMS.includes(item.label)) {
        return {
          ...item,
          children: item.children
            ? filterMenuRecursively(item.children, true)
            : [],
        };
      }
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuRecursively(item.children);
        if (filteredChildren.length > 0)
          return { ...item, children: filteredChildren };
      }
      return null;
    })
    .filter((item: any) => item !== null);
};

const capitalizeAndRemoveUnderscores = (str: string) => {
  return str
    .split("_")
    .map((word, index) => {
      if (index === 0 && word.toLowerCase() === "etl") {
        return "ETL";
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export default function MobileDrawer() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const userData = useSelector(selectUserData);
  const userMenuFiltered = useSelector(selectUserMenuFiltered);
  const loggedInUserDetails = Object.values(
    userData?.data?.user_details as Record<string, any>,
  );

  // Stable reference — only recomputes when the raw menu data changes, NOT on every render.
  // Without this, filteredMenu being a new array on every render caused the useEffect below
  // to fire on every re-render, immediately resetting openMenu back to the current path's
  // section and making it impossible to switch menus while on biz_development/dashboard.
  const filteredMenu = useMemo(
    () => filterMenuRecursively(userMenuFiltered || []),
    [userMenuFiltered],
  );

  useEffect(() => {
    setSelectedItem(location.pathname);
    const active = getActiveSectionLabel(location.pathname, filteredMenu);
    if (active && active !== openMenu) {
      setOpenMenu(active);
    }
    // eslint-disable-next-line
  }, [location.pathname, filteredMenu]);

  const handleOpenDrawer = () => {
    const active = getActiveSectionLabel(location.pathname, filteredMenu);
    setOpenMenu(active);
    setOpenSubMenu(null);
    setDrawerOpen(true);
  };

  const handleToggleMenu = (menuKey: string) => {
    setOpenMenu(openMenu === menuKey ? null : menuKey);
    setOpenSubMenu(null);
  };

  const handleToggleSubMenu = (subMenuKey: string) => {
    setOpenSubMenu(openSubMenu === subMenuKey ? null : subMenuKey);
  };

  const handleMenuItemClick = (path: string) => {
    const workspace = searchParams.get("workspace");
    navigate({ pathname: path, search: workspace ? `workspace=${workspace}` : "" });
    setDrawerOpen(false);
    setOpenSubMenu(null);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await loginService.logout();
      dispatch(clearSelectedWorkspace());
      dispatch(resetWorkspaceState());

      searchParams.delete("workspace");
      setSearchParams(searchParams, { replace: true });
      setDrawerOpen(false);
      navigate("/logout");
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Logout failed. Please try again.",
          type: "error",
        }),
      );
    } finally {
      setIsLoggingOut(false);
    }
  };

  const renderMenuItem = (item: any, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isMenuOpen = openMenu === item.label;
    const isSubMenuOpen = openSubMenu === item.label;
    const isActive = selectedItem?.includes(item.path || "");
    // const paddingLeft = `${16 + level * 16}px`;

    return (
      <Box key={item.label} sx={{ mx: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              if (hasChildren && level === 0) {
                handleToggleMenu(item.label);
              } else if (hasChildren && level === 1) {
                handleToggleSubMenu(item.label);
              } else {
                handleMenuItemClick(item.path);
              }
            }}
            sx={{
              userSelect: "none",
              cursor: "pointer",
              color: isActive
                ? colors.Brand[700].$value
                : colors.GrayLightMode[700].$value,
            }}
          >
            {/* {level > 0 && (
              <ListItemIcon>
                <ChevronRightIcon />
              </ListItemIcon>
            )} */}
            <ListItemText
              primary={capitalizeAndRemoveUnderscores(item.label)}
              primaryTypographyProps={{
                fontSize:
                  level === 0
                    ? typography.Size.Text.md.$value
                    : typography.Size.Text.sm.$value,
                fontWeight:
                  level === 0
                    ? typography.Font_weight.Regular.$value
                    : typography.Font_weight.Regular.$value,
              }}
            />
            {hasChildren && (
              <ExpandMoreIcon
                sx={{
                  transform:
                    (level === 0 && isMenuOpen) ||
                    (level === 1 && isSubMenuOpen)
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  transition: "transform 0.3s",
                }}
              />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && level === 0 && isMenuOpen && (
          <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children.map((child: any) => renderMenuItem(child, 1))}
            </List>
          </Collapse>
        )}

        {hasChildren && level === 1 && isSubMenuOpen && (
          <Collapse in={isSubMenuOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children.map((child: any) => renderMenuItem(child, 2))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Mobile AppBar */}
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          zIndex: 1100,
          bgcolor: colors.Base.White.$value,
          color: colors.GrayDarkMode[900].$value,
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "64px",
            px: spacing.Spacing.sm.$value,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              // edge="start"
              color="inherit"
              aria-label="menu"
              size="large"
              onClick={handleOpenDrawer}
              sx={{ mr: spacing.Spacing.xs.$value }}
            >
              <MenuIcon />
            </IconButton>
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: typography.Size.Display.xs.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                }}
              >
                Aladdin
              </Typography>
            </Link>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton size="large" color="inherit">
              <SearchIcon />
            </IconButton>
            {/* <IconButton size="small" color="inherit">
              <NotificationsIcon />
            </IconButton> */}
            {/* <IconButton size="small" color="inherit">
              <Avatar sx={{ width: 32, height: 32, fontSize: "0.875rem" }}>
                {loggedInUserDetails[1]?.charAt(0) || "U"}
              </Avatar>
            </IconButton> */}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 340,
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTapHighlightColor: "transparent",
            "& *": { WebkitTapHighlightColor: "transparent" },
            borderTopLeftRadius: spacing.Radius.none.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            borderBottomRightRadius: spacing.Radius.xl.$value,
            borderBottomLeftRadius: spacing.Radius.none.$value,
          },
        }}
      >
        {/* Drawer Header */}
        {/* <Box
          sx={{
            p: 2,
            bgcolor: "primary.main",
            color: "white",
            minHeight: 140,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                mr: 2,
                bgcolor: "secondary.main",
              }}
            >
              {loggedInUserDetails[1]?.charAt(0) || "U"}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {loggedInUserDetails[1] || "User"}
              </Typography>
              <Typography variant="caption">
                {loggedInUserDetails[3] || "email@example.com"}
              </Typography>
            </Box>
          </Box>
        </Box> */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h5">Menu</Typography>
          </Box>

          <IconButton
            aria-label="Logout"
            onClick={() => setDrawerOpen(false)}
            size="large"
            sx={{
              width: spacing.Spacing["5xl"].$value,
              height: spacing.Spacing["5xl"].$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            <MenuOpenIcon />
          </IconButton>
        </Box>

        <WorkspaceDropdown isDrawerOpen={drawerOpen} />

        {/* Main Menu */}
        <List sx={{ flex: 1, overflow: "auto", mt: 1 }}>
          {/* Home Menu Item */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleMenuItemClick("/")}
              sx={{
                userSelect: "none",
                cursor: "pointer",
                color:
                  location.pathname === "/"
                    ? colors.Brand[700].$value
                    : colors.GrayLightMode[700].$value,
                mx: 1,
              }}
            >
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          {filteredMenu?.map((item: any) => renderMenuItem(item))}
        </List>

        {/* Admin Menu (at bottom if exists) */}
        {/* {userMenuFiltered?.some((item: any) => item?.label === "admin") && (
          <>
            <Divider />
            <List>
              {userMenuFiltered
                ?.filter((item: any) => item?.label === "admin")
                .map((item: any) => renderMenuItem(item))}
            </List>
          </>
        )} */}

        <Divider sx={{ mt: "auto", mx: 2 }} />

        {/* Logout Button */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  width: spacing.Spacing["5xl"].$value,
                  height: spacing.Spacing["5xl"].$value,
                  mr: 1.5,
                  bgcolor: "secondary.main",
                }}
              >
                {loggedInUserDetails[1]?.charAt(0) || "U"}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {loggedInUserDetails[1] || "User"}
                </Typography>
                <Typography variant="caption" letterSpacing={0}>
                  {loggedInUserDetails[3] || "email@example.com"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <IconButton
            aria-label="Logout"
            onClick={handleLogout}
            disabled={isLoggingOut}
            size="large"
            sx={{
              width: spacing.Spacing["5xl"].$value,
              height: spacing.Spacing["5xl"].$value,
              mr: 2,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Box>

        {/* <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              disabled={isLoggingOut}
              sx={{
                "&:hover": { bgcolor: "#9C27B014" },
              }}
            >
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary={isLoggingOut ? "Logging out..." : "Logout"}
              />
            </ListItemButton>
          </ListItem>
        </List> */}

        <Box sx={{ p: 2, pt: "10px", textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.Base.Black.$value,
              fontSize: "0.8rem",
            }}
          >
            Powered by Optival LTD
          </Typography>
        </Box>
      </Drawer>

      {/* Spacer for AppBar */}
      <Toolbar sx={{ minHeight: "56px !important" }} />
    </>
  );
}
